import { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import confetti from 'canvas-confetti';
import soundManager from '../utils/soundManager';

const RewardUnlockModal = ({ isOpen, onClose, rewardItem }) => {
  const [animationPhase, setAnimationPhase] = useState('entering'); // entering, revealing, revealed

  useEffect(() => {
    if (isOpen && rewardItem) {
      setAnimationPhase('entering');

      // Phase 0: Anticipation — Pokeball materializing (rising chime)
      soundManager.playMenuOpen();

      // Phase 1: Pokeball/chest appears (0-1s)
      const timer1 = setTimeout(() => {
        setAnimationPhase('revealing');
        // Subtle drumroll-ish tab tick to build tension
        soundManager.playTabSwitch();
      }, 1000);
      
      // Phase 2: Item revealed (1-2s) with fanfare sound
      const timer2 = setTimeout(() => {
        setAnimationPhase('revealed');
        // Play reveal fanfare based on rarity
        if (rewardItem.rarity === 'rare') {
          soundManager.playItemRevealRare();
        } else {
          soundManager.playItemReveal();
        }
        // Trigger confetti
        triggerRewardConfetti(rewardItem.rarity);
      }, 2000);
      
      return () => {
        clearTimeout(timer1);
        clearTimeout(timer2);
      };
    }
  }, [isOpen, rewardItem]);

  const triggerRewardConfetti = (rarity) => {
    const colors = rarity === 'rare' 
      ? ['#FFD700', '#FF69B4', '#9333EA', '#FFFFFF']
      : ['#10B981', '#3B82F6', '#F59E0B'];
    
    const particleCount = rarity === 'rare' ? 150 : 80;
    
    // Burst from center
    confetti({
      particleCount,
      spread: 100,
      origin: { y: 0.5, x: 0.5 },
      colors,
      startVelocity: 45,
      gravity: 0.8
    });
    
    // Extra sparkles for rare items
    if (rarity === 'rare') {
      setTimeout(() => {
        confetti({
          particleCount: 50,
          spread: 120,
          origin: { y: 0.4, x: 0.3 },
          colors: ['#FFD700', '#FFFFFF']
        });
        confetti({
          particleCount: 50,
          spread: 120,
          origin: { y: 0.4, x: 0.7 },
          colors: ['#FFD700', '#FFFFFF']
        });
      }, 200);
    }
  };

  if (!isOpen || !rewardItem) return null;

  return (
    <div 
      className="fixed inset-0 z-[100] flex items-center justify-center p-4"
      data-testid="reward-unlock-modal"
    >
      {/* Backdrop with blur */}
      <div 
        className="absolute inset-0 bg-black/70 backdrop-blur-sm animate-fade-in"
        onClick={() => {
          soundManager.playButtonClick();
          onClose();
        }}
      />

      {/* Modal Content */}
      <div className="relative z-10 w-full max-w-sm">
        {/* Close button */}
        <button
          onClick={() => {
            soundManager.playButtonClick();
            onClose();
          }}
          className="absolute -top-2 -right-2 w-8 h-8 rounded-full bg-white/20 
                     flex items-center justify-center text-white hover:bg-white/30 z-20"
        >
          <X className="w-4 h-4" />
        </button>

        {/* Animated Container */}
        <div 
          className={`
            relative rounded-3xl p-8
            bg-gradient-to-br ${rewardItem.color}
            shadow-2xl transform transition-all duration-500
            ${animationPhase === 'entering' ? 'scale-90 opacity-80' : 'scale-100 opacity-100'}
          `}
        >
          {/* Glow effect */}
          <div 
            className={`
              absolute inset-0 rounded-3xl
              ${rewardItem.rarity === 'rare' ? 'animate-glow-rare' : 'animate-glow-common'}
            `}
            style={{
              background: rewardItem.rarity === 'rare'
                ? 'radial-gradient(circle, rgba(255,215,0,0.3) 0%, transparent 70%)'
                : 'radial-gradient(circle, rgba(255,255,255,0.2) 0%, transparent 70%)'
            }}
          />

          {/* Content */}
          <div className="relative text-center">
            {/* Phase 1 & 2: Pokeball spinning */}
            {animationPhase !== 'revealed' && (
              <div 
                className={`
                  text-8xl mb-4
                  ${animationPhase === 'entering' ? 'animate-pokeball-wobble' : 'animate-pokeball-open'}
                `}
              >
                {animationPhase === 'entering' ? '🔮' : '✨'}
              </div>
            )}

            {/* Phase 3: Item revealed */}
            {animationPhase === 'revealed' && (
              <>
                {/* Item emoji with bounce */}
                <div className="text-8xl mb-4 animate-item-reveal">
                  {rewardItem.emoji}
                </div>

                {/* Title */}
                <h2 className="text-2xl font-outfit font-black text-white mb-2 drop-shadow-lg">
                  Novo Item!
                </h2>

                {/* Item name */}
                <div className="text-3xl font-outfit font-black text-white mb-2 drop-shadow-lg">
                  {rewardItem.name}
                </div>

                {/* Rarity badge */}
                {rewardItem.rarity === 'rare' && (
                  <div className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-yellow-400/30 border border-yellow-300/50 mb-3">
                    <span className="text-sm">⭐</span>
                    <span className="text-xs font-bold text-yellow-200 uppercase tracking-wider">Raro</span>
                  </div>
                )}

                {/* Description */}
                <p className="text-white/90 font-nunito text-lg mb-6">
                  {rewardItem.description}
                </p>

                {/* Action button */}
                <button
                  onClick={() => {
                    soundManager.playButtonClick();
                    onClose();
                  }}
                  className="px-8 py-3 rounded-full bg-white text-slate-800 font-bold
                             hover:scale-105 transition-transform duration-200
                             shadow-lg hover:shadow-xl"
                >
                  Guardar na Mochila
                </button>
              </>
            )}

            {/* Loading text during animation */}
            {animationPhase !== 'revealed' && (
              <p className="text-white/80 font-nunito text-lg animate-pulse">
                {animationPhase === 'entering' ? 'Sorteando recompensa...' : 'Revelando...'}
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default RewardUnlockModal;
